import { get, orderBy } from 'lodash';
import { Moment, MomentInput } from 'moment';
import { Publish } from '../_shared/interfaces/publish';
import {
  distinct,
  kindOf,
  occurredAt,
  Product,
  statisticsOf,
} from '../_shared/lib/device-events';

// Where statistics rows belong in time, for the charts, the "today" views and the reports. How each
// product reports its periods is in src/_shared/lib/device-events.ts; this decides how the displays
// use it. published_at and sk only say when a row arrived: a utility unit sends a day's totals when it
// is next switched on, which can be months later, and a chair from firmware 30 queues its statistics
// (AERFW-79). Particle can also hand us the same row twice.

// A statistics row on the day it covers for a daily (a moment inside that day), otherwise at the end
// of its period.
export const statisticsWhen = (
  product: Product | undefined,
  row: Publish
): Moment => {
  const s = statisticsOf(product, row);
  return (s.kind === 'daily' && s.day ? s.day : s.end.at).clone();
};

// Any row on a timeline: statistics as above, anything else when it happened.
export const eventTime = (product: Product | undefined, row: Publish): Moment =>
  kindOf(row) === 'statistics'
    ? statisticsWhen(product, row)
    : occurredAt(product, row).at.clone();

// Each event once, newest first.
export const newestFirst = (product: Product | undefined, rows: Publish[]) =>
  orderBy(
    distinct(product, rows),
    (r) => eventTime(product, r).valueOf(),
    'desc'
  );

// The sessions (chairs, handpieces) that ended on `day`, each once, whenever they arrived. Keeps the
// order it is given.
export const sessionsEndedOn = (
  product: Product | undefined,
  rows: Publish[],
  day: Moment
): Publish[] =>
  distinct(
    product,
    rows.filter((r) => {
      const s = statisticsOf(product, r);
      return s.kind === 'session' && s.end.at.isSame(day, 'day');
    })
  );

// When a session began: its start where its product reports one that can be trusted (a chair from
// firmware 30), otherwise its end less its length (data.total_session_time[3], in seconds).
export const sessionStart = (
  product: Product | undefined,
  row: Publish
): Moment => {
  const s = statisticsOf(product, row);
  return s.start
    ? s.start.clone()
    : s.end.at
        .clone()
        .subtract(Number(get(row, 'data.total_session_time.3', 0)), 'seconds');
};

// For reports, which are about what the rows describe: the rows that happened between start and end
// inclusive, or all of them without a range, each once.
export const statisticsBetween = (
  product: Product | undefined,
  rows: Publish[],
  start?: MomentInput,
  end?: MomentInput
): Publish[] =>
  distinct(
    product,
    start === undefined || end === undefined
      ? rows
      : rows.filter((r) =>
          statisticsWhen(product, r).isBetween(start, end, undefined, '[]')
        )
  );

// The kind of a statistics row by name, or its raw type for one its product does not define
// (AER-1811: the report matches selections by name).
export const statisticsKindName = (
  product: Product | undefined,
  row: Publish
): string =>
  statisticsOf(product, row).kind ||
  `${get(row, 'data.type', get(row, 'data.statistics_type'))}`;

// The report's statistics type selection, as names. The report page used to send the other
// products' numbers, '0' daily and '1' hourly, and a page loaded before this change still can.
export const toStatisticsTypeNames = (selected: string[]): string[] =>
  selected.map((s) => (s === '0' ? 'daily' : s === '1' ? 'hourly' : s));
